(() => {
  if (window.ProductAsVariant) return;

  const SELECTORS = {
    root: '[data-pav-root]',
    card: '[data-pav-card]',
    productSection: '.js-product',
    productLink: '.js-prod-link, .card-link, .quick-add-view-btn, .b2b-card__title a, .b2b-card__media a',
    cardTitle: '.card__title .card-link, .b2b-card__title a',
    cardPrice: '.price--bottom, .b2b-card__price',
    pdpTitle: '.product-title',
    pdpPrice: '.product-price .product-info__price',
    formInput: 'form[action*="/cart/add"] [name="id"], .js-product-form [name="id"]',
    addButton: '[name="add"]',
    quickAdd: '.js-quick-add',
    image: '.card__main-image, .b2b-card__main-image, .product-media img'
  };

  const state = new WeakMap();
  const productCache = new Map();

  const decode = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value || '';
    return textarea.value;
  };

  const normalise = (value) => String(value || '').trim().toLowerCase();

  const getContainer = (root) => (
    root.closest(SELECTORS.card)
    || root.closest(SELECTORS.productSection)
    || root.closest('product-card')
    || root.closest('.b2b-card-wrapper')
    || document
  );

  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      const variant = data.variants[0];

      const colorOption = data.options.find(o =>
        o.name.toLowerCase().includes('color') || o.name.toLowerCase().includes('colour')
      );
      const colorValue = colorOption ? data.variants[0][`option${data.options.indexOf(colorOption) + 1}`] : data.title;

      const product = {
        id: data.id,
        handle: data.handle,
        title: data.title,
        url: data.url,
        variantId: variant.id,
        variantTitle: variant.title,
        sku: variant.sku,
        available: variant.available,
        price: variant.price,
        compareAtPrice: variant.compare_at_price || 0,
        image: data.featured_image,
        imageAlt: data.title,
        color: colorValue,
      };

      productCache.set(handle, product);
      return product;
    } catch {
      return null;
    }
  };

  const fetchAllProducts = async (handles) => {
    const results = await Promise.all(handles.map(fetchProduct));
    return results.filter(Boolean);
  };

  const setButtonState = (container, product) => {
    container.querySelectorAll(SELECTORS.addButton).forEach((button) => {
      button.disabled = !product.available;
      button.setAttribute('aria-disabled', product.available ? 'false' : 'true');
      const label = button.querySelector('.quick-add-btn-text, .pointer-events-none:last-child, span:last-child');
      if (!label) return;
      if (product.available && button.dataset.addToCartText) {
        label.textContent = button.dataset.addToCartText;
      } else if (!product.available && window.theme?.strings?.soldOut) {
        label.textContent = theme.strings.soldOut;
      }
    });
  };

  const updateBadges = (container, product) => {
    container.querySelectorAll('.product-label--sale').forEach((badge) => {
      if (product.compareAtPrice > product.price && product.compareAtPrice > 0) {
        badge.textContent = `${Math.round(((product.compareAtPrice - product.price) * 100) / product.compareAtPrice)}%`;
        badge.closest('.product-label-wrapper')?.removeAttribute('hidden');
      } else {
        badge.closest('.product-label-wrapper')?.setAttribute('hidden', '');
      }
    });

    container.querySelectorAll('.product-label--sold-out').forEach((badge) => {
      const wrapper = badge.closest('.product-label-wrapper');
      if (!wrapper) return;
      product.available ? wrapper.setAttribute('hidden', '') : wrapper.removeAttribute('hidden');
    });
  };

  const updateImage = (container, product) => {
    if (!product.image) return;
    const cardImage = container.querySelector('.card__main-image, .b2b-card__main-image');
    if (cardImage) {
      cardImage.src = product.image;
      cardImage.srcset = '';
      cardImage.alt = product.imageAlt || product.title;
    } else {
      const pdpImage = container.querySelector('.product-media img');
      if (pdpImage) {
        pdpImage.src = product.image;
        pdpImage.srcset = '';
        pdpImage.alt = product.imageAlt || product.title;
      }
    }
  };

  const updateLinks = (container, product, replaceUrl) => {
    container.querySelectorAll(SELECTORS.productLink).forEach((link) => {
      link.href = product.url;
    });
    container.querySelectorAll(SELECTORS.quickAdd).forEach((button) => {
      button.dataset.productUrl = product.url;
      button.dataset.itemId = product.sku || product.variantId;
      button.dataset.itemName = product.title;
      button.dataset.itemVariant = product.variantTitle;
      button.dataset.price = product.price / 100;
    });
    if (replaceUrl && window.history?.replaceState) {
      window.history.replaceState({}, '', product.url);
    }
  };

  const updateText = (container, product, context) => {
    const titleSelector = context === 'pdp' ? SELECTORS.pdpTitle : SELECTORS.cardTitle;
    const title = container.querySelector(titleSelector);
    if (title) title.textContent = product.title;
  };

  const updateForms = (container, product) => {
    container.querySelectorAll(SELECTORS.formInput).forEach((input) => {
      input.value = product.variantId;
      input.disabled = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const updateSelectedControls = (root, product) => {
    root.querySelectorAll('[data-pav-option]').forEach((button) => {
      const value = normalise(button.dataset.pavValue);
      const selected = normalise(product.color) === value;
      button.classList.toggle('product-as-variant__swatch--selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  };

  const applyProduct = (root, product, options = {}) => {
    const container = getContainer(root);
    const context = root.dataset.pavContext || 'card';

    updateSelectedControls(root, product);
    updateImage(container, product);
    updateLinks(container, product, context === 'pdp' && options.updateUrl !== false);
    updateText(container, product, context);
    updateForms(container, product);
    setButtonState(container, product);
    updateBadges(container, product);

    state.set(root, { ...state.get(root), product });

    container.dispatchEvent(new CustomEvent('product-as-variant:change', {
      bubbles: true,
      detail: { product }
    }));
  };

  const initRoot = async (root) => {
    if (root.dataset.pavInitialized === 'true') return;
    root.dataset.pavInitialized = 'true';

    let handles = [];
    try {
      handles = JSON.parse(decode(root.dataset.pavHandles));
    } catch {
      return;
    }

    if (!handles.length) return;

    const currentId = Number(root.dataset.pavCurrentProductId);
    state.set(root, { handles, product: null, loaded: false });

    // Fetch current product immediately for initial state
    const currentHandle = handles[0];
    const currentProduct = await fetchProduct(currentHandle);
    if (!currentProduct) return;

    applyProduct(root, currentProduct, { updateUrl: false });

    root.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-pav-option]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();

      const clickedValue = normalise(button.dataset.pavValue);

      // Load all products on first interaction
      const current = state.get(root);
      if (!current.loaded) {
        const allProducts = await fetchAllProducts(handles);
        state.set(root, { ...current, products: allProducts, loaded: true });
      }

      const { products } = state.get(root);
      const match = products.find(p => normalise(p.color) === clickedValue) || products[0];
      if (match) applyProduct(root, match);
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTORS.root).forEach(initRoot);
  };

  window.ProductAsVariant = { init };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) init(node);
        });
      });
    });
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();